/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ECharts, EChartsOption, SeriesOption } from "echarts";
import { NgxTippyModule } from "ngx-tippy-wrapper";
import { filter, forkJoin, map, of, Subject, switchMap, takeUntil, tap } from "rxjs";
import { AccordionGroupComponent } from "../../shared/components/accordion-group/accordion-group.component";
import { AccordionComponent } from "../../shared/components/accordion-group/accordion/accordion.component";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { ChartComponent } from "../../shared/components/chart/chart.component";
import { CheckboxComponent } from "../../shared/components/checkbox/checkbox.component";
import { DropdownComponent } from "../../shared/components/dropdown/dropdown.component";
import { InputComponent } from "../../shared/components/input/input.component";
import { KpiComponent } from "../../shared/components/kpi/kpi.component";
import { MapComponent } from "../../shared/components/map/map.component";
import { LineChart } from "../../shared/models/http/charts/line-chart/line-chart.model";
import { MapChart } from "../../shared/models/http/charts/map-chart/map-chart.model";
import { MapRegion } from "../../shared/models/http/charts/map-chart/map-region.model";
import { CarbonIntensitySummary } from "../../shared/models/http/what-if/carbon-intensity/carbon-intensity-summary.model";
import { LayerConfigurations } from "../../shared/models/http/what-if/layer/layer-info.model";
import { ScenarioLayer } from "../../shared/models/http/what-if/scenario/scenario-layer.model";
import { ChartResponse } from "../../shared/models/ui/charts.model";
import { ScenarioToEditConfig } from "../../shared/models/ui/scenario-to-edit-config.model";
import { SelectOption } from "../../shared/models/ui/select-option.model";
import { StorageService } from "../../shared/services/storage.service";
import { WhatIfCarbonIntensityService } from "../../shared/services/what-if-carbon-intensity.service";
import { WhatIfLayerService } from "../../shared/services/what-if-layer.service";
import { WhatIfScenarioService } from "../../shared/services/what-if-scenario.service";
import { WhatIfService } from "../../shared/services/what-if.service";
import {
	CLOUD_PROVIDERS,
	COLORS,
	DEFAULT_PUE,
	PATHS,
	REGIONS,
	REGIONS_MAP
} from "../../shared/utils/constants";
import { getRangesColorForMapItemAreaColor } from "../../shared/utils/echarts/chart-utils";

@Component({
	selector: "globalictcc-what-if-configuration",
	standalone: true,
	imports: [
		ButtonComponent,
		InputComponent,
		DropdownComponent,
		AccordionGroupComponent,
		AccordionComponent,
		ChartComponent,
		MapComponent,
		CheckboxComponent,
		ReactiveFormsModule,
		NgxTippyModule,
		KpiComponent
	],
	templateUrl: "./what-if-configuration.component.html",
	styleUrl: "./what-if-configuration.component.scss"
})
export class WhatIfConfigurationComponent implements OnInit, OnDestroy {
	public scenarioAndLayerForm = new FormGroup({
		scenario: new FormControl<number | null>(null),
		layer: new FormControl("")
	});

	public configurationForm = new FormGroup({
		country: new FormControl("", [Validators.required]),
		morningShiftCPU: new FormControl<number>(1, [Validators.min(1), Validators.max(100)]),
		afternoonShiftCPU: new FormControl<number>(1, [Validators.min(1), Validators.max(100)]),
		eveningShiftCPU: new FormControl<number>(1, [Validators.min(1), Validators.max(100)]),
		nightShiftCPU: new FormControl<number>(1, [Validators.min(1), Validators.max(100)]),
		morningShiftGPU: new FormControl<number>(0, [Validators.min(0), Validators.max(100)]),
		afternoonShiftGPU: new FormControl<number>(0, [Validators.min(0), Validators.max(100)]),
		eveningShiftGPU: new FormControl<number>(0, [Validators.min(0), Validators.max(100)]),
		nightShiftGPU: new FormControl<number>(0, [Validators.min(0), Validators.max(100)]),
		cloudProvider: new FormControl({ value: "", disabled: true }, [Validators.required]),
		pue: new FormControl(DEFAULT_PUE, [
			Validators.required,
			Validators.min(1),
			Validators.max(3)
		])
	});

	public lineOverrides: EChartsOption = {
		yAxis: {
			name: ""
		},
		series: {
			color: COLORS.YELLOW_100,
			smooth: true,
			showSymbol: false
		}
	};

	public countriesOptions: SelectOption[] = REGIONS;
	public regionsMap: Record<string, string> = REGIONS_MAP;
	public scenariosOptions: SelectOption[] = [];
	public layersOptions: SelectOption[] = [];
	public cloudProvidersOptions: SelectOption[] = CLOUD_PROVIDERS;
	public isCloudApplication: boolean = false;
	public isSingleLayerEdit: boolean = false;
	public scenarioToEditConfig?: ScenarioToEditConfig;
	public spatialShiftingData?: CarbonIntensitySummary;
	public spatialShiftingMapChart: ChartResponse<MapChart> | null;
	public temporalShiftingLineChart: ChartResponse<LineChart> | null;
	public isSpatialShiftingMapChartLoading: boolean = false;
	public isTemporalShiftingLineChartLoading: boolean = false;
	public mapConfigOverrides: EChartsOption;

	private _resourceType: string = "";
	private _applicationId: string = "";
	private _predictionWindow: string = "";
	private _scenarioId: number;
	private _layerId: string = "";
	private _currentLayer?: ScenarioLayer;
	private _region: string = "";
	private _regionsMap: ECharts;
	private _tenantId: string = "";
	private _possibleLayers: ScenarioLayer[] = [];
	private _destroy$ = new Subject<void>();

	constructor(
		private _whatIfService: WhatIfService,
		private _whatIfScenarioService: WhatIfScenarioService,
		private _whatIfLayerService: WhatIfLayerService,
		private _whatIfCarbonIntensityService: WhatIfCarbonIntensityService,
		private _storageService: StorageService,
		private _route: ActivatedRoute,
		private _router: Router
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();

		this._onScenarioChange();
		this._onLayerChange();
		this._onRegionChange();
		this._onQueryParamsChange();
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Handles changes to the scenario selection in the form.
	 *
	 * This method subscribes to the `valueChanges` observable of the scenario control
	 * in the `scenarioAndLayerForm` form group. When the scenario value changes, it
	 * triggers a series of actions:
	 *
	 * 1. If the scenario ID or application ID is not available, it returns an observable
	 *    of `null`.
	 * 2. If both IDs are available, it calls the `getScenarioInfo` method of the
	 *    `_whatIfScenarioService` to fetch scenario information.
	 * 3. Upon receiving the scenario information, it updates the `_possibleLayers` property
	 *    and the `layersOptions` property with the layers from the scenario.
	 *
	 * The subscription is automatically cleaned up when the component is destroyed,
	 * using the `takeUntil` operator with the `_destroyed$` observable.
	 *
	 * @private
	 */
	private _onScenarioChange() {
		this.scenarioAndLayerForm.controls.scenario.valueChanges
			.pipe(
				switchMap(scenarioId => {
					if (!scenarioId || !this._applicationId) return of(null);

					return this._whatIfScenarioService.getScenarioInfo(
						this._applicationId,
						scenarioId,
						this._tenantId
					);
				}),
				filter(Boolean),
				tap({
					next: scenario => {
						this._possibleLayers = scenario.layers;

						if (this.isSingleLayerEdit) {
							this.layersOptions = this._possibleLayers.map(layer => ({
								label: layer.name,
								value: layer.name
							}));

							this.scenarioAndLayerForm.controls.layer.setValue(this._layerId);
						} else {
							this.scenarioAndLayerForm.controls.layer.disable({
								emitEvent: false
							});
							this.layersOptions = [{ label: "ALL", value: "all" }];
							this.scenarioAndLayerForm.controls.layer.setValue("all");
						}

						this._updateLayersData();
					}
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Handles changes to the layer selection in the scenario and layer form.
	 *
	 * This method subscribes to the `valueChanges` observable of the `layer` control
	 * in the `scenarioAndLayerForm` form group. When the value changes, it checks if
	 * the component is in single layer edit mode. If it is, it finds the corresponding
	 * layer from the `_possibleLayers` array and updates the `configurationForm` with
	 * the CPU and GPU load values for different shifts (morning, afternoon, evening, night).
	 *
	 * The subscription is automatically unsubscribed when the component is destroyed
	 * using the `takeUntil` operator with the `_destroyed$` observable.
	 *
	 * @private
	 */
	private _onLayerChange() {
		this.scenarioAndLayerForm.controls.layer.valueChanges
			.pipe(
				map(layerId => layerId || undefined),
				filter(layerId => !!layerId && this._possibleLayers.length > 0),
				tap({ next: layerId => this._updateLayersData(layerId) }),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Initializes the map with the given ECharts instance.
	 * Stores the instance in the `_regionsMap` property and highlights the region after a short delay.
	 * There is no others event in ECharts to know when the map is fully loaded.
	 *
	 * @param {ECharts} echartsInstance - The instance of ECharts to be initialized.
	 */
	public onMapInit(echartsInstance: ECharts) {
		this._regionsMap = echartsInstance;
		this._highlightRegion.bind(this);
	}

	/**
	 * Handles the map click event and updates the form control value.
	 *
	 * @param params - The event parameters containing information about the clicked region.
	 *                 Expected to include a `shortName` property when cast to `MapRegion`.
	 */
	public onMapClick(params: unknown) {
		const { shortName: clickedRegionShortName } = (params as SeriesOption).data as MapRegion;

		if (clickedRegionShortName)
			this.configurationForm.controls.country.setValue(clickedRegionShortName);
	}

	/**
	 * Handles changes to the region selection in the configuration form.
	 *
	 * This method subscribes to the `valueChanges` observable of the `country` control
	 * in the `configurationForm`. When the region changes, it performs the following actions:
	 * - If the region, application ID, or tenant ID is not available, it returns an observable of `null`.
	 * - Updates the internal `_region` property with the new region value.
	 * - Retrieves spatial and temporal shifting data by calling `_getScendarioDataObservables`.
	 * - Uses `forkJoin` to wait for both observables to complete and then processes the results.
	 * - Updates the `spatialShiftingData` property and calls `_updateCharts` with the new data.
	 *
	 * The subscription is automatically cleaned up when the component is destroyed.
	 *
	 * @private
	 */
	private _onRegionChange() {
		this.configurationForm.controls.country.valueChanges
			.pipe(
				filter(Boolean),
				switchMap(region => {
					if (!this._applicationId || !this._tenantId) return of(null);

					this.isTemporalShiftingLineChartLoading = true;

					this._region = region;

					const { getSpatialShiftingData, getTemporalShifting } =
						this._getScendarioDataObservables();

					return forkJoin([getSpatialShiftingData, getTemporalShifting]);
				}),
				filter(Boolean),
				tap({
					next: selectedScenarioData => {
						this._highlightRegion();

						if (!selectedScenarioData.length) return;

						const [spatialShiftingData, temporalShifting] = selectedScenarioData;

						if (!spatialShiftingData || (!temporalShifting && this.isSingleLayerEdit))
							return;

						this.spatialShiftingData = spatialShiftingData;

						this._updateCharts(this.spatialShiftingMapChart, temporalShifting);
						this.isTemporalShiftingLineChartLoading = false;
					},
					finalize: () => (this.isTemporalShiftingLineChartLoading = false)
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Highlights a specific region on the regions map by dispatching a "select" action.
	 *
	 * This method checks if both the `_regionsMap` and `_region` properties are defined.
	 * If they are, it triggers a selection action on the map for the region specified
	 * by the `_region` property, using the corresponding name from the `REGIONS_MAP` mapping.
	 *
	 * @private
	 */
	private _highlightRegion() {
		if (this._regionsMap && this._region) {
			this._regionsMap.dispatchAction({
				type: "select",
				seriesIndex: 0,
				name: this.regionsMap[this._region]
			});
		}
	}

	/**
	 * Handles the change event for the cloud application toggle.
	 *
	 * @param checked - A boolean indicating whether the cloud application is enabled (true) or disabled (false).
	 */
	public onCloudApplicationChange(checked: boolean) {
		this.isCloudApplication = checked;

		if (this.isCloudApplication) {
			this.configurationForm.controls.pue.disable({ emitEvent: false });
			this.configurationForm.controls.cloudProvider.enable({ emitEvent: false });
		} else {
			this.configurationForm.controls.pue.enable({ emitEvent: false });
			this.configurationForm.controls.cloudProvider.disable({ emitEvent: false });
		}
	}

	/**
	 * Handles changes in the query parameters of the route.
	 *
	 * This method subscribes to the query parameters observable and performs the following actions:
	 * - Extracts `applicationId`, `scenarioId`, and `layerId` from the query parameters.
	 * - Sets the `isSingleLayerEdit` flag based on the presence of `layerId`.
	 * - Updates the form controls for scenario and layer with the extracted values.
	 * - If `applicationId` or `_tenantId` is missing, it returns an observable of `null`.
	 * - Otherwise, it fetches the necessary data using the `_whatIfService` and other observables.
	 * - Combines the fetched data using `forkJoin` and processes the response.
	 * - If the response is empty, it navigates back.
	 * - Otherwise, it updates the component state with the fetched data and updates the charts.
	 *
	 * @private
	 */
	private _onQueryParamsChange() {
		this._route.queryParams
			.pipe(
				filter(queryParams => !!queryParams["applicationId"]),
				switchMap(queryParams => {
					if (!this._tenantId) return of(null);

					const { resourceType, applicationId, predictionWindow, scenarioId, layerId } =
						queryParams;

					this._resourceType = resourceType;
					this._applicationId = applicationId;
					this._predictionWindow = predictionWindow;
					this._scenarioId = scenarioId;
					this._layerId = layerId;

					this.isSingleLayerEdit = !!this._layerId;

					this.isSpatialShiftingMapChartLoading = true;
					this.isTemporalShiftingLineChartLoading = true;

					return this._whatIfService.getUserWhatIfData(
						this._applicationId,
						this._tenantId
					);
				}),
				switchMap(whatIfData => {
					if (!whatIfData) {
						this.onGoBack();
						return of(null);
					}

					this.scenariosOptions = whatIfData.scenarios.map(scenario => ({
						label: scenario.name,
						value: scenario.id.toString()
					}));

					this.scenarioAndLayerForm.controls.scenario.setValue(this._scenarioId);
					this.scenarioAndLayerForm.controls.layer.setValue(this._layerId);

					const { getSpatialShiftingMap } = this._getScendarioDataObservables();

					return getSpatialShiftingMap;
				}),
				tap({
					next: spatialShiftingMap => {
						this._updateCharts(spatialShiftingMap, this.temporalShiftingLineChart);
						this.isSpatialShiftingMapChartLoading = false;
						this.isTemporalShiftingLineChartLoading = false;
					},
					finalize: () => {
						this.isSpatialShiftingMapChartLoading = false;
						this.isTemporalShiftingLineChartLoading = false;
					}
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Handles the selection event.
	 *
	 * This method checks if the required `_applicationId` and `_tenantId` are present.
	 * If they are, it retrieves the selected scenario data observables and subscribes to them.
	 * Upon receiving the data, it updates the charts with the selected scenario data.
	 */
	public onSelect() {
		if (!this._applicationId || !this._tenantId) return;

		this.isSpatialShiftingMapChartLoading = true;
		this.isTemporalShiftingLineChartLoading = true;

		const { getSpatialShiftingData, getTemporalShifting } = this._getScendarioDataObservables();

		return forkJoin([getSpatialShiftingData, getTemporalShifting])
			.pipe(
				tap({
					next: selectedScenarioData => {
						const [spatialShiftingData, temporalShifting] = selectedScenarioData;

						this.spatialShiftingData = spatialShiftingData || undefined;

						this._updateCharts(this.spatialShiftingMapChart, temporalShifting);
						this.isSpatialShiftingMapChartLoading = false;
						this.isTemporalShiftingLineChartLoading = false;
					},
					finalize: () => {
						this.isSpatialShiftingMapChartLoading = false;
						this.isTemporalShiftingLineChartLoading = false;
					}
				})
			)
			.subscribe();
	}

	/**
	 * Retrieves observables for scenario data based on the current configuration form and region.
	 *
	 * @private
	 * @returns An object containing the following observables:
	 * - `getSpatialShiftingMap`: Observable for all carbon intensity data.
	 * - `getSpatialShiftingData`: Observable for monthly average carbon intensity data by region.
	 * - `getTemporalShifting`: Observable for hourly average carbon intensity data by region if in single layer edit mode, otherwise an empty observable.
	 */
	private _getScendarioDataObservables() {
		const region = this.configurationForm.controls.country.value || this._region;

		const getSpatialShiftingMap =
			this._whatIfCarbonIntensityService.getAllCarbonIntensityData();

		const getSpatialShiftingData = region
			? this._whatIfCarbonIntensityService.getMonthlyAverageByRegion(region)
			: of(null);

		const getTemporalShifting =
			this.isSingleLayerEdit && region
				? this._whatIfCarbonIntensityService.getHourlyAverageByRegion(region)
				: of(null);

		return {
			getSpatialShiftingMap,
			getSpatialShiftingData,
			getTemporalShifting
		};
	}

	/**
	 * Updates the form with data from the specified layer.
	 * If no layerId is provided, it uses the current layer ID.
	 *
	 * @param {string} [layerId] - The ID of the layer to update data from. If not provided, the current layer ID is used.
	 * @private
	 */
	private _updateLayersData(layerId?: string) {
		this._currentLayer = this._possibleLayers.find(layer => {
			return layer.name === layerId;
		});

		if (this._currentLayer) {
			this._region = this._currentLayer.region as string;
			this._highlightRegion();

			this.configurationForm.patchValue({
				country: this._region,
				morningShiftCPU: this._currentLayer.cpuLoad.morning || 1,
				afternoonShiftCPU: this._currentLayer.cpuLoad.afternoon || 1,
				eveningShiftCPU: this._currentLayer.cpuLoad.evening || 1,
				nightShiftCPU: this._currentLayer.cpuLoad.night || 1,
				morningShiftGPU: this._currentLayer.gpuLoad.morning || 0,
				afternoonShiftGPU: this._currentLayer.gpuLoad.afternoon || 0,
				eveningShiftGPU: this._currentLayer.gpuLoad.evening || 0,
				nightShiftGPU: this._currentLayer.gpuLoad.night || 0,
				pue: this._currentLayer.pue || 1,
				cloudProvider: this._currentLayer.cloudProvider || ""
			});

			this.onCloudApplicationChange(!!this._currentLayer.cloudProvider);
		}
	}

	/**
	 * Updates the charts with the provided spatial and temporal shifting data.
	 *
	 * @param spatialShiftingMap - The map chart data for spatial shifting. The title property will be removed from this object.
	 * @param temporalShifting - The line chart data for temporal shifting. If provided, the title property will be removed from this object.
	 */
	private _updateCharts(
		spatialShiftingMap: ChartResponse<MapChart> | null,
		temporalShifting: ChartResponse<LineChart> | null
	) {
		if (spatialShiftingMap) {
			this.mapConfigOverrides = {
				visualMap: { pieces: getRangesColorForMapItemAreaColor() }
			};

			this.spatialShiftingMapChart = spatialShiftingMap;

			this.countriesOptions = spatialShiftingMap.chart.mapRegions.map(region => ({
				label: region.name,
				value: region.shortName
			}));

			this.regionsMap = this.countriesOptions.reduce(
				(acc, option) => ({ ...acc, [option.value]: option.label }),
				{}
			);
		} else {
			this.spatialShiftingMapChart = {
				chart: {} as MapChart,
				hasError: true
			};
		}

		if (temporalShifting) this.temporalShiftingLineChart = temporalShifting;
		else {
			this.temporalShiftingLineChart = {
				chart: {} as LineChart,
				hasError: true
			};
		}
	}

	/**
	 * Navigates back to the previous page using the environment service.
	 * This method is typically called when the user wants to go back to the previous view.
	 */
	public onGoBack() {
		const resourceType = this._resourceType;
		const applicationId = this._applicationId;
		const predictionWindow = this._predictionWindow;
		const scenarioId = this._scenarioId;
		const layerId = this._layerId;

		this._router.navigate([PATHS.WHAT_IF], {
			queryParams: {
				resourceType,
				applicationId,
				predictionWindow,
				scenarioId,
				layerId
			}
		});
	}

	/**
	 * Applies the configuration based on the form values and the current state.
	 *
	 * This method checks if the necessary IDs (_applicationId, _scenarioId, _tenantId) are present.
	 * If any of these IDs are missing, the method returns early.
	 *
	 * It constructs a `layersConfig` object with the region and optionally includes
	 * cloud provider or PUE (Power Usage Effectiveness) based on whether the application
	 * is a cloud application or not.
	 *
	 * If the configuration is for a single layer edit, it ensures that all CPU and GPU shift values
	 * (morning, afternoon, evening, night) are present. If any of these values are null, the method returns early.
	 *
	 * The method then sets the CPU and GPU load configurations in the `layersConfig` object.
	 *
	 * Depending on whether it is a single layer edit or not, it calls the appropriate service method
	 * (`editScenariolayer` or `editAllScenarioLayers`) to apply the configuration and subscribes to the result.
	 * On successful completion, it navigates back by calling `onGoBack`.
	 */
	public onApplyConfiguration() {
		if (!this._applicationId || !this._scenarioId || !this._tenantId) return;

		const layersConfig: LayerConfigurations = {
			region: this.configurationForm.controls.country.value || this._region,
			...(this.isCloudApplication &&
				this.configurationForm.controls.cloudProvider.value && {
					cloudProvider: this.configurationForm.controls.cloudProvider.value
				}),
			...(!this.isCloudApplication &&
				this.configurationForm.controls.pue.value && {
					pue: +this.configurationForm.controls.pue.value
				})
		};

		if (this.isSingleLayerEdit) {
			layersConfig.cpuLoad = {
				morning: this.configurationForm.controls.morningShiftCPU.value || 1,
				afternoon: this.configurationForm.controls.afternoonShiftCPU.value || 1,
				evening: this.configurationForm.controls.eveningShiftCPU.value || 1,
				night: this.configurationForm.controls.nightShiftCPU.value || 1
			};

			layersConfig.gpuLoad = {
				morning: this.configurationForm.controls.morningShiftGPU.value || 0,
				afternoon: this.configurationForm.controls.afternoonShiftGPU.value || 0,
				evening: this.configurationForm.controls.eveningShiftGPU.value || 0,
				night: this.configurationForm.controls.nightShiftGPU.value || 0
			};
		}

		if (this.isSingleLayerEdit) {
			this._whatIfLayerService
				.editScenariolayer(
					this._applicationId,
					this._scenarioId,
					this._layerId,
					this._tenantId,
					layersConfig
				)
				.pipe(tap({ next: () => this.onGoBack() }))
				.subscribe();
		} else {
			this._whatIfLayerService
				.editAllScenarioLayers(
					this._applicationId,
					this._scenarioId,
					this._tenantId,
					layersConfig
				)
				.pipe(tap({ next: () => this.onGoBack() }))
				.subscribe();
		}
	}

	/**
	 * Refreshes the map by fetching all carbon intensity data and updating the spatial shifting map chart.
	 *
	 * This method uses the `_whatIfCarbonIntensityService` to retrieve the data and updates the
	 * `spatialShiftingMapChart` property with the new data. The data retrieval process is managed
	 * using RxJS operators, and the subscription is automatically unsubscribed when the component
	 * is destroyed.
	 */
	public onMapRefresh() {
		this.isSpatialShiftingMapChartLoading = true;

		this._whatIfCarbonIntensityService
			.getAllCarbonIntensityData()
			.pipe(
				tap({
					next: spatialShiftingMapChart => {
						this._updateCharts(spatialShiftingMapChart, this.temporalShiftingLineChart);
						this.isSpatialShiftingMapChartLoading = false;
					},
					finalize: () => (this.isSpatialShiftingMapChartLoading = false)
				})
			)
			.subscribe();
	}

	/**
	 * Refreshes the temporal shifting chart by fetching the hourly average carbon intensity data
	 * for the current region. If no region is selected, the function returns early.
	 *
	 * This method uses the `_whatIfCarbonIntensityService` to get the data and updates the
	 * `temporalShiftingLineChart` property with the fetched data. The data fetching process
	 * is terminated when the `_destroy$` observable emits a value.
	 */
	public onTemporalShiftingChartRefresh() {
		if (!this._region) return;

		this.isTemporalShiftingLineChartLoading = true;

		this._whatIfCarbonIntensityService
			.getHourlyAverageByRegion(this._region)
			.pipe(
				tap({
					next: temporalShiftingLineChart => {
						this._updateCharts(this.spatialShiftingMapChart, temporalShiftingLineChart);
						this.isTemporalShiftingLineChartLoading = false;
					},
					finalize: () => (this.isTemporalShiftingLineChartLoading = false)
				})
			)
			.subscribe();
	}
}
